import { UNIT_FLOAT_DIVISOR, hashBits24, unitHash } from "../random.ts";
import { DAY_GLOW, LIGHT_INTENSITY_RANGE, LIGHT_RADIUS_RANGE } from "./limits.ts";
import type { Flicker } from "./types.ts";

/**
 * A frame's light (ADR-141's amendment of 2026-11-01, decisions 1, 4 and 5): where on the art it sits, how far and how
 * bright it reaches, its colour, how it flickers, when it is on, the art shown while it is off and the energy source
 * switch. Set once on the frame, it lights every placed copy. Pure: the reads, the flicker and the day rule are the same
 * in the studio, Daedalus, the page and the server.
 */

/**
 * The named flickers a light may take (decision H21 gives the campfire and the torch).
 */
export const FLICKER_KINDS = Object.freeze({
  none: Object.freeze({ amount: 0, hz: 0, wild: 0, wobble: 0 }),
  candle: Object.freeze({ amount: 0.38, hz: 10, wild: 0.35, wobble: 0.6 }),
  "oil-lamp": Object.freeze({ amount: 0.14, hz: 3.5, wild: 0.25, wobble: 0.3 }),
  "magic-lamp": Object.freeze({ amount: 0, hz: 0, wild: 0, wobble: 0 }),
  hearth: Object.freeze({ amount: 0.36, hz: 5, wild: 0.8, wobble: 1.5 }),
  campfire: Object.freeze({ amount: 0.45, hz: 7, wild: 1, wobble: 2.5 }),
  torch: Object.freeze({ amount: 0.36, hz: 8.5, wild: 0.6, wobble: 1.2 }),
}) satisfies Readonly<Record<string, Flicker & { readonly wild: number; readonly wobble: number }>>;

/**
 * The reach and brightness a light takes when its flicker is chosen, where a kind has its own: a candle is small and dim.
 * A designer may change either after.
 */
export const FLICKER_LOOKS: Readonly<Partial<Record<keyof typeof FLICKER_KINDS, { readonly radius: number; readonly strength: number; readonly rgb?: readonly [number, number, number] }>>> = Object.freeze({
  candle: Object.freeze({ radius: 40, strength: 0.6 }),
  "magic-lamp": Object.freeze({ radius: 96, strength: 0.9, rgb: Object.freeze([168, 208, 255] as const) }),
});

/**
 * Flicker names an earlier build wrote, read as the kind that replaced them: the one `lamp` split into the oil lamp (warm,
 * flickering) and the magic lamp (pure and steady), and data that said `lamp` meant the flickering one.
 */
const FLICKER_RENAMED: Readonly<Record<string, keyof typeof FLICKER_KINDS>> = Object.freeze({ lamp: "oil-lamp" });

/**
 * A flicker's name.
 */
export type FlickerKind = keyof typeof FLICKER_KINDS;

/**
 * The flicker names in the order a chooser lists them.
 */
export const FLICKER_KIND_NAMES: readonly FlickerKind[] = Object.freeze(Object.keys(FLICKER_KINDS) as FlickerKind[]);

/**
 * When a light is on: `always` (a faint glow by day, full in the dark) or `dusk` (dusk to dawn: off by day).
 */
export type LightWhen = "always" | "dusk";

/**
 * The `when` values in the order a chooser lists them.
 */
export const LIGHT_WHENS: readonly LightWhen[] = Object.freeze(["always", "dusk"]);

/**
 * How far outside a frame's art, in its pixels, a light's anchor may sit.
 */
export const LIGHT_ANCHOR_REACH = 64;

/**
 * The largest anchor coordinate, in frame pixels, a light may name.
 */
const ANCHOR_MAX = 2048;

const SOURCE_KIND = /^[a-z][a-z0-9-]{0,23}$/;
const FRAME_ID = /^[^/]+\/[^/]+\/(0|[1-9][0-9]*)$/;
const FIELDS = new Set(["x", "y", "radius", "rgb", "strength", "flicker", "when", "unlit", "source"]);

/**
 * A frame's light. `x` and `y` are the anchor in the frame's pixels from its top-left; `unlit` is the frame shown while
 * the light is off (absent: the art does not change); `source` is the energy source kind, absent when the switch is off.
 */
export interface FrameLight {
  readonly x: number;
  readonly y: number;
  readonly radius: number;
  readonly rgb: readonly [number, number, number];
  readonly strength: number;
  readonly flicker: FlickerKind;
  readonly when: LightWhen;
  readonly unlit?: string;
  readonly source?: string;
}

/**
 * Reads a light from JSON.
 *
 * @param value The value.
 * @param path Where it was read, for the error.
 * @returns The light.
 * @throws Error naming the first field that is missing, unknown or out of range.
 */
export function readFrameLight(value: unknown, path: string): FrameLight {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    if (!FIELDS.has(key)) {
      throw new Error(`${path}.${key} is not a field of a light`);
    }
  }
  const anchor = (key: "x" | "y"): number => {
    const read = record[key];
    if (typeof read !== "number" || !Number.isInteger(read) || read < -LIGHT_ANCHOR_REACH || read > ANCHOR_MAX) {
      throw new Error(`${path}.${key} must be a whole number of pixels from ${-LIGHT_ANCHOR_REACH} to ${ANCHOR_MAX}`);
    }
    return read;
  };
  const x = anchor("x");
  const y = anchor("y");
  const radius = record["radius"];
  if (typeof radius !== "number" || !Number.isInteger(radius) || radius < LIGHT_RADIUS_RANGE.min || radius > LIGHT_RADIUS_RANGE.max) {
    throw new Error(`${path}.radius must be a whole number from ${LIGHT_RADIUS_RANGE.min} to ${LIGHT_RADIUS_RANGE.max}`);
  }
  const rgb = record["rgb"];
  if (!Array.isArray(rgb) || rgb.length !== 3 || !rgb.every((c) => typeof c === "number" && Number.isInteger(c) && c >= 0 && c <= 255)) {
    throw new Error(`${path}.rgb must be three whole numbers from 0 to 255`);
  }
  const strength = record["strength"];
  if (typeof strength !== "number" || !Number.isFinite(strength) || strength < LIGHT_INTENSITY_RANGE.min || strength > LIGHT_INTENSITY_RANGE.max) {
    throw new Error(`${path}.strength must be from ${LIGHT_INTENSITY_RANGE.min} to ${LIGHT_INTENSITY_RANGE.max}`);
  }
  const named = record["flicker"];
  const flicker = typeof named === "string" && Object.hasOwn(FLICKER_RENAMED, named) ? FLICKER_RENAMED[named] : named;
  if (typeof flicker !== "string" || !Object.hasOwn(FLICKER_KINDS, flicker)) {
    throw new Error(`${path}.flicker must be one of ${FLICKER_KIND_NAMES.join(", ")}`);
  }
  const when = record["when"];
  if (when !== "always" && when !== "dusk") {
    throw new Error(`${path}.when must be always or dusk`);
  }
  const unlit = record["unlit"];
  if (unlit !== undefined && (typeof unlit !== "string" || !FRAME_ID.test(unlit))) {
    throw new Error(`${path}.unlit must be a frame id`);
  }
  const source = record["source"];
  if (source !== undefined && (typeof source !== "string" || !SOURCE_KIND.test(source))) {
    throw new Error(`${path}.source must be a lower-case name of at most 24 letters, digits and dashes`);
  }
  return {
    x,
    y,
    radius,
    rgb: [rgb[0] as number, rgb[1] as number, rgb[2] as number],
    strength,
    flicker: flicker as FlickerKind,
    when,
    ...(unlit === undefined ? {} : { unlit: unlit as string }),
    ...(source === undefined ? {} : { source: source as string }),
  };
}

/**
 * The one slot `flickerFactor` reads `flickerInto`'s answer from.
 */
const ONE = new Float64Array(1);

/**
 * `flickerAt` in scalar arguments, which allocates nothing, for a caller that runs it per light per frame.
 *
 * @param timeMs Time in milliseconds.
 * @param seed The light's seed, a non-negative integer.
 * @param amount The flicker's amount, 0 to 1.
 * @param hz The flicker's rate in hertz.
 * @param wild How wild the flicker is, 0 to 1: a second, faster layer of noise and occasional flares and dips; 0 is the
 *   plain smooth noise.
 * @returns A factor from `1 - amount` to 1, equal to `flickerAt(timeMs, seed, { amount, hz })` when `wild` is 0; a wild
 *   flicker may dip lower and flare above 1.
 */
export function flickerFactor(timeMs: number, seed: number, amount: number, hz: number, wild = 0): number {
  flickerInto(ONE, 0, timeMs, seed, amount, hz, wild);
  return ONE[0] as number;
}

/**
 * `flickerFactor` written into a slot of a typed array: every step is inline and every hash is a small integer, so a caller
 * that runs it per light per frame allocates nothing whether or not the engine inlines it. Each noise step is `valueNoise`
 * written out in place, and must stay equal to it (the golden tests in `frame-light.test.ts` hold both to the same values).
 *
 * @param out Where the factor goes.
 * @param index The slot.
 * @param timeMs Time in milliseconds.
 * @param seed The light's seed, a non-negative integer.
 * @param amount The flicker's amount, 0 to 1.
 * @param hz The flicker's rate in hertz.
 * @param wild How wild the flicker is, 0 to 1.
 */
export function flickerInto(out: Float32Array | Float64Array, index: number, timeMs: number, seed: number, amount: number, hz: number, wild: number): void {
  if (amount === 0 || hz === 0) {
    out[index] = 1;
    return;
  }
  const x = (timeMs * hz) / 1000;
  const cell = Math.floor(x);
  const a = hashBits24(seed >>> 0, cell >>> 0) / UNIT_FLOAT_DIVISOR;
  const t = x - cell;
  const base = a + (hashBits24(seed >>> 0, (cell + 1) >>> 0) / UNIT_FLOAT_DIVISOR - a) * t * t * (3 - 2 * t);
  if (wild <= 0) {
    out[index] = 1 - amount * base;
    return;
  }
  const fastX = x * FAST_RATIO;
  const fastCell = Math.floor(fastX);
  const fastSeed = (seed ^ FAST_SALT) >>> 0;
  const c = hashBits24(fastSeed, fastCell >>> 0) / UNIT_FLOAT_DIVISOR;
  const u = fastX - fastCell;
  const fast = c + (hashBits24(fastSeed, (fastCell + 1) >>> 0) / UNIT_FLOAT_DIVISOR - c) * u * u * (3 - 2 * u);
  const noise = base * (1 - wild * 0.5) + fast * wild * 0.5;
  const slow = x / FLARE_CELL;
  const flareCell = Math.floor(slow);
  const chance = hashBits24((seed ^ FLARE_SALT) >>> 0, flareCell >>> 0) / UNIT_FLOAT_DIVISOR;
  let flare = 0;
  if (chance > 1 - FLARE_CHANCE * wild) {
    const envelope = 1 - Math.abs(2 * (slow - flareCell) - 1);
    flare = (chance * 1e4) % 1 < 0.5 ? -amount * envelope : amount * FLARE_UP * envelope;
  }
  const value = 1 - amount * noise + flare;
  out[index] = value < 0 ? 0 : value;
}

/**
 * How much faster a wild flicker's second layer of noise runs than its first.
 */
const FAST_RATIO = 2.7;

/**
 * The word folded into a light's seed for its second layer of noise, so that layer is not the first one sped up.
 */
const FAST_SALT = 0x9e3779b9;

/**
 * The word folded into a light's seed for its flares and dips, so where they fall is independent of both noise layers.
 */
const FLARE_SALT = 0x51ed270b;
/**
 * A wild flicker's flares and dips: the noise cells one may span, the share of those spans that hold one at full
 * wildness, and how far a flare rises above full strength as a share of the flicker's amount.
 */
const FLARE_CELL = 6;
const FLARE_CHANCE = 0.45;
const FLARE_UP = 0.6;

/**
 * Smooth value noise at a point: a hash of the seed and each whole step, eased between them.
 *
 * @param x The point.
 * @param seed The seed.
 * @returns 0 to 1.
 */
function valueNoise(x: number, seed: number): number {
  const cell = Math.floor(x);
  const a = unitHash(seed >>> 0, cell >>> 0);
  const b = unitHash(seed >>> 0, (cell + 1) >>> 0);
  const t = x - cell;
  return a + (b - a) * t * t * (3 - 2 * t);
}

/**
 * How much farther a light reaches while it flares above full strength (P2.25-T29): the cut is already whole at strength 1,
 * so a flare shows as a wider pool, `FLARE_REACH` of the reach for each unit over 1.
 *
 * @param share The light's share this frame, its strength times its flicker.
 * @returns 1 at or below full strength, more above it.
 */
export function flareReach(share: number): number {
  return share > 1 ? 1 + (share - 1) * FLARE_REACH : 1;
}

/**
 * The share of a light's reach a flare adds for each unit of strength over 1.
 */
export const FLARE_REACH = 0.8;

/**
 * The small drift of a wild light's glow along one axis, in its wobble's pixels: smooth noise at a slow rate, so a fire's
 * pool sways a little and does not hold still. Allocates nothing.
 *
 * @param timeMs Time in milliseconds.
 * @param seed The light's seed.
 * @param axis 0 for across, 1 for down.
 * @returns -1 to 1, times the wobble the caller applies.
 */
export function wobbleAt(timeMs: number, seed: number, axis: 0 | 1): number {
  return valueNoise((timeMs * WOBBLE_HZ) / 1000, (seed + (axis + 1) * 7919) >>> 0) * 2 - 1;
}

/**
 * How fast a wild light's glow drifts, in noise steps a second: slow, so the pool sways and does not shake.
 */
const WOBBLE_HZ = 1.6;

/**
 * A light's seed from its prop's id: an FNV-1a fold of the id's code units, kept to 24 bits so it is exact in a
 * `Float32Array`.
 *
 * @param id The prop's id.
 * @returns A whole number below 2^24.
 */
export function lightSeed(id: string): number {
  let h = 0x811c9dc5;
  for (let index = 0; index < id.length; index += 1) {
    h = Math.imul(h ^ id.charCodeAt(index), 0x01000193) >>> 0;
  }
  return (h ^ (h >>> 24)) & 0xffffff;
}

/**
 * How much of its strength a light gives at a sky (decision 5): an `always` light never below `DAY_GLOW` and full in the
 * dark (decision D5); a `dusk` light off while the dark share is below `DAY_GLOW`, then following it. Indoors a `dusk` light
 * never goes out and every light follows the `always` rule against the room's held light, so a room with a dim lights it
 * fully and a room with none shows only the day glow (note 11).
 *
 * @param ambient The sky's ambient, 0 to 1.
 * @param nightLevel The ambient the dark holds.
 * @param when When the light is on.
 * @param indoor Whether the map is indoors; its `nightLevel` is then its held ambient.
 * @returns 0 to 1.
 */
export function lightOn(ambient: number, nightLevel: number, when: LightWhen, indoor: boolean): number {
  const span = 1 - nightLevel;
  const dark = Math.min(1, span > 0 ? Math.max(0, (1 - ambient) / span) : 0);
  if (when === "always" || indoor) return Math.max(DAY_GLOW, dark);
  return dark < DAY_GLOW ? 0 : dark;
}

/**
 * Where a placed copy's light sits in world pixels: the prop's bottom-left (`x`, `y + 1` in tiles) plus the anchor,
 * mirrored across the frame when the prop is. The point is whole when the prop and the anchor are; the light view rounds
 * it to whole world pixels for both the cut and the glow, so the two never part.
 *
 * @param light The frame's light.
 * @param prop The placed prop's tile position and mirroring.
 * @param frame The frame's size in pixels.
 * @param tileSize Tile size in pixels.
 * @param out Written with the point, so a caller in a loop allocates nothing.
 * @returns `out`.
 */
export function lightPoint(light: Pick<FrameLight, "x" | "y">, prop: { readonly x: number; readonly y: number; readonly flipX?: boolean; readonly flipY?: boolean }, frame: { readonly w: number; readonly h: number }, tileSize: number, out: { x: number; y: number }): { x: number; y: number } {
  const left = prop.x * tileSize;
  const top = (prop.y + 1) * tileSize - frame.h;
  out.x = left + (prop.flipX === true ? frame.w - light.x : light.x);
  out.y = top + (prop.flipY === true ? frame.h - light.y : light.y);
  return out;
}
